import React, { useRef, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import SessionTabStrip from './components/SessionTabStrip';
import { useSessionManager } from './context/SessionManagerContext';
import { useSocket } from './context/SocketContext';
import Login from './components/Login';
import Logout from './components/Logout';
import KioskRegister from './components/KioskRegister';
import KioskTask from './components/KioskTask';
import KioskKit from './components/KioskKit';
import PrivateRoute from './components/PrivateRoute';
import MainLayout from './layouts/MainLayout';
import Billing from "./components/Billing";

import Branches from "./components/Branches";
import Services from './components/Services';
import Exercises from './components/Exercises';
import Recomms from './components/Recomms';
import Orgs from './components/Orgs';
import Cal from './components/Cal';
import Resource from './components/Cal'; // Adjust the path as needed
import LeaveUpdates from "./components/LeaveUpdates";
import AuditLogViewer from "./components/AuditLogViewer";
import Callleads from './components/Callleads';
import TaskManagerPage from './components/TaskManagerPage';
import ChecklistsDashboard from './components/ChecklistsDashboard';
import ManageExpenses from './components/ManageExpenses';
import MobileExpenses from './components/MobileExpenses';


//import ListPatients from './components/ListPatients';
import ListPatientsWrapper from './components/ListPatientWrapper';

import Consultation from './components/Consultation';
import PatientDashboard from './components/PatientDashboard';
import Staff from './components/Staff';
import UserMgmt from './components/UserMgmt';
import Roles from './components/Roles';
import Permissions from './components/Permissions';
import Inventory from './components/Inventory';
import TherapistDashboard from './components/TherapistDashboard';
import MonthlyDashboard from './components/MonthlyDashboard';
import Payroll from './components/Payroll';
import TextScanner from './components/TextScanner';
import PatientIrregular from './components/PatientIrregular';
//-----------------------
import AttendanceWrapper from './components/solution/AttendanceWrapper';
import AttendanceScan from './components/AttendanceScan';
import AttendanceReport from './components/AttendanceReport';
import Policies from './components/Policies';
import SupervisorDashboard from './components/solution/SupervisorDashboard';
import CheckListsDashboard from './components/solution/CheckListsDashboard';
import Sites from './components/solution/Sites';
import OpsDashboard from './components/solution/OpsDashboard';
import Reports from './components/solution/Reports';
import KIT from './components/solution/KIT';
import FlowDashboard from './components/solution/FlowDashboard';
import MappingsDashboard from './components/solution/MappingsDashboard';
import PaymentsLedger from './components/solution/PaymentsLedger';
import Resources from './components/Resources';
import NotificationsPage from './components/NotificationsPage';
import AppShareCard from './components/AppShareCard';
import LiveSessionsMonitor from './components/LiveSessionsMonitor';
import BPSelection from './components/BPSelection';
import SessionIpadView from './components/SessionIpadView';
import OnlineDashboard from './components/solution/OnlineDashboard';
import SaadhakDashboard from './components/SaadhakDashboard';
import MyDocuments from './components/MyDocuments';
import ChangePassword from './components/ChangePassword';
import DanglingEntries from './components/DanglingEntries';
import MyReports from './components/MyReports';
import MyConsultations from './components/MyConsultations';


// ── Expenses: show mobile version on small screens ───────────────────────────
function ExpensesRouter() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 480);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile ? <MobileExpenses /> : <ManageExpenses />;
}

// ── Global ding player ────────────────────────────────────────────────────────
// Plays the ding audio for sessions that become isDinging while the therapist
// is NOT on that patient's SessionIpadView (e.g. on Billing, TherapistDashboard…).
// When they ARE on the session view, SessionIpadView's own audio element handles it.
function GlobalDingPlayer() {
  const sm       = useSessionManager();
  const { socketRef } = useSocket() || {};
  const location = useLocation();
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const tapRef   = useRef(null);

  const sessions = sm?.sessions || {};

  // Card currently being viewed (if any)
  const viewingCard = location.pathname.startsWith('/SessionIpadView/')
    ? decodeURIComponent(location.pathname.slice('/SessionIpadView/'.length).split('?')[0])
    : null;

  const stopGlobalDing = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (tapRef.current) { document.removeEventListener('pointerdown', tapRef.current); tapRef.current = null; }
  }, []);

  // Acknowledge ding for ONE background session at a time.
  // If multiple sessions are dinging, the audio replays for the next one on the next render cycle,
  // so the trainer is forced to acknowledge each client individually.
  const acknowledgeDinging = useCallback(() => {
    const first = Object.entries(sessions).find(([card, s]) => s.isDinging && card !== viewingCard);
    if (!first) return;
    const [card, s] = first;
    sm?.acknowledgeCard(card);
    // Broadcast isDinging:false but KEEP autoStoppedIdx so remote devices can still
    // trigger markExerciseDone when SIV is opened for this patient.
    socketRef?.current?.emit('session:state', {
      patientCard: card,
      elapsedSec:     s.elapsedSec     ?? 0,
      activeIdx:      s.activeIdx      ?? 0,
      isPaused:       s.isPaused       ?? true,
      isDinging:      false,
      autoStoppedIdx: s.autoStoppedIdx ?? null, // preserve — don't clear
      waitingForResume: s.waitingForResume ?? false,
      pauseReason:    s.pauseReason    ?? null,
      exercises:      s.exercises      ?? [],
      persist:        false,
    });
  }, [sessions, viewingCard, sm, socketRef]); // eslint-disable-line

  // Check if any non-viewed session is dinging — play if audio not already playing, stop if none dinging
  useEffect(() => {
    const anyDinging = Object.entries(sessions).some(([card, s]) => s.isDinging && card !== viewingCard);
    const isPlaying = audioRef.current && !audioRef.current.paused;

    if (anyDinging && !isPlaying && audioRef.current) {
      stopGlobalDing();
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 1;
      audioRef.current.loop = true;
      audioRef.current.play().catch(() => {});
      // Stop on any screen tap — acknowledge + broadcast to other devices
      const onTap = () => { stopGlobalDing(); acknowledgeDinging(); };
      tapRef.current = onTap;
      document.addEventListener('pointerdown', onTap);
      // Auto-stop after 30s as safety net
      timerRef.current = setTimeout(() => { stopGlobalDing(); acknowledgeDinging(); }, 30000);
    } else if (!anyDinging && isPlaying) {
      stopGlobalDing();
    }
  }); // intentionally no dep array — runs every render

  // Cleanup on unmount
  useEffect(() => () => stopGlobalDing(), [stopGlobalDing]);

  return <audio ref={audioRef} src="/session-over-new.mp3" preload="auto" playsInline style={{ display: 'none' }} />;
}

function App() {
  return (
    <ErrorBoundary>
    <>
      {/* Session tab strip — fixed bottom bar, always visible on every route */}
      <SessionTabStrip />
      {/* Plays ding audio for sessions that complete while not on their SessionIpadView */}
      <GlobalDingPlayer />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/Logout" element={<Logout />} />

        {/* Public pages — no token / PrivateRoute needed */}
        <Route path="/SaadhakPortal" element={<SaadhakDashboard />} />
        <Route path="/kiosk/register" element={<KioskRegister />} />
        <Route path="/kiosk/expenses" element={<MobileExpenses />} />
        <Route path="/kiosk/task" element={<KioskTask />} />
        <Route path="/kiosk/kit" element={<KioskKit />} />

        <Route element={<PrivateRoute permission="billing.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Billing" element={<Billing />} />
          </Route>
        </Route>
        <Route element={<PrivateRoute permission="payments.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/PaymentsLedger" element={<PaymentsLedger />} />
          </Route>
        </Route>
       <Route element={<PrivateRoute permission="branches.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Branches" element={<Branches />} />
          </Route>
        </Route>

         <Route element={<PrivateRoute permission="exercises.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Exercises" element={<Exercises />} />
          </Route>
        </Route>


        <Route element={<PrivateRoute permission="orgs.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Orgs" element={<Orgs />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="services.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Services" element={<Services/>} />
          </Route>
        </Route>

       {/* LeaveUpdates now lives inside MyAttendance as a tab — redirect old URL */}
        <Route path="/LeaveUpdates" element={<Navigate to="/MyAttendance?tab=leaves" replace />} />

        <Route element={<PrivateRoute permission="appointments.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Cal" element={<Cal />} />
          </Route>
        </Route>


         <Route element={<PrivateRoute permission="recommendations.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Recomms" element={<Recomms />} />
          </Route>
        </Route>


        <Route element={<PrivateRoute permission="inventory.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Inventory" element={<Inventory />} />
          </Route>
        </Route>

   <Route element={<PrivateRoute permission="audit.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/AuditLogViewer" element={<AuditLogViewer />} />
          </Route>
        </Route>


    <Route path="/calendar-popup" element={<Resource />} />

    <Route element={<PrivateRoute permission="expenses.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/ManageExpenses" element={<ExpensesRouter />} />
          </Route>
        </Route>
         <Route element={<PrivateRoute permission="checklist.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/TaskManagerPage" element={<TaskManagerPage />} />
            <Route path="/ChecklistsDashboard" element={<ChecklistsDashboard />} />
          </Route>
        </Route>

             <Route element={<PrivateRoute permission="reports.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/MonthlyDashboard" element={<MonthlyDashboard />} />
          </Route>
        </Route>

<Route element={<PrivateRoute permission="leads.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Callleads" element={<Callleads />} />
          </Route>
        </Route>


        <Route element={<PrivateRoute permission="patients.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/ListPatients" element={<ListPatientsWrapper />} />
          </Route>
        </Route>
        <Route element={<PrivateRoute permission="consultation.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Consultation" element={<Consultation />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="patients.dashboard" />}>
          <Route element={<MainLayout />}>
            <Route path="/PatientDashboard" element={<PatientDashboard />} />
          </Route>
        </Route>


        <Route element={<PrivateRoute />}>
          {/* SaadhakDashboard has its own full layout — no MainLayout wrapper */}
          <Route path="/SaadhakDashboard" element={<SaadhakDashboard />} />
        </Route>

        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/TherapistDashboard" element={<TherapistDashboard />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="users.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/usermgmt" element={<UserMgmt />} />
          </Route>
        </Route>
        <Route element={<PrivateRoute permission="users.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Staff" element={<Staff />} />
          </Route>
        </Route>
        <Route element={<PrivateRoute permission="roles.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Roles" element={<Roles />} />
          </Route>
        </Route>
        {/*
        <Route element={<PrivateRoute permission="users.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Permissions" element={<Permissions />} />
          </Route>
        </Route> */}

        <Route element={<PrivateRoute permission="payroll.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Payroll" element={<Payroll />} />
          </Route>
        </Route>


        <Route element={<PrivateRoute permission="patients.irregular" />}>
          <Route element={<MainLayout />}>
            <Route path="/PatientIrregular" element={<PatientIrregular />} />
          </Route>
        </Route>

         <Route element={<PrivateRoute permission="scanner.use" />}>
          <Route element={<MainLayout />}>
            <Route path="/TextScanner" element={<TextScanner />} />
          </Route>
        </Route>



         <Route element={<PrivateRoute permission="attendance.edit" />}>
          <Route element={<MainLayout />}>
            <Route path="/Attendance" element={<AttendanceWrapper />} />
          </Route>
        </Route>

        {/* ── Simple QR attendance (all staff) ── */}
        <Route element={<PrivateRoute permission="attendance.view_own" />}>
          <Route element={<MainLayout />}>
            <Route path="/MyAttendance" element={<AttendanceScan />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="attendance.view_all" altPermission="attendance.review" />}>
          <Route element={<MainLayout />}>
            <Route path="/AttendanceReport" element={<AttendanceReport />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="attendance.policy" />}>
          <Route element={<MainLayout />}>
            <Route path="/Policies" element={<Policies />} />
            <Route path="/AttendancePolicies" element={<Policies />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/CheckListsDashboard" element={<CheckListsDashboard />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/SupervisorDashboard" element={<SupervisorDashboard />} />
          </Route>
        </Route>


        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/OpsDashboard" element={<OpsDashboard />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="sites.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Sites" element={<Sites />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/Reports" element={<Reports />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="leads.kit" />}>
          <Route element={<MainLayout />}>
            <Route path="/KIT" element={<KIT />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="flows.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/FlowDashboard" element={<FlowDashboard />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="mappings.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Mappings" element={<MappingsDashboard />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="resources.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Resources" element={<Resources />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="sessions.monitor" />}>
          <Route element={<MainLayout />}>
            <Route path="/LiveSessions" element={<LiveSessionsMonitor />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="notifications.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Notifications" element={<NotificationsPage />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/MyReports" element={<MyReports />} />
            <Route path="/MyConsultations" element={<MyConsultations />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="app.share" />}>
          <Route element={<MainLayout />}>
            <Route path="/ShareApp" element={<AppShareCard />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="sessions.conduct" />}>
          <Route element={<MainLayout />}>
            <Route path="/SessionIpadView/:patientCard" element={<SessionIpadView />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="exercises.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/BPSelection" element={<BPSelection />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="admin.scan" />}>
          <Route element={<MainLayout />}>
            <Route path="/DanglingEntries" element={<DanglingEntries />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute permission="online.view" />}>
          <Route element={<MainLayout />}>
            <Route path="/Online" element={<OnlineDashboard />} />
          </Route>
        </Route>

        {/* ── Self-service pages (all logged-in users) ── */}
        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/MyDocuments" element={<MyDocuments />} />
            <Route path="/ChangePassword" element={<ChangePassword />} />
          </Route>
        </Route>

        <Route path="*" element={<Login />} />
      </Routes>
    </>
    </ErrorBoundary>
  );
}

export default App;
